import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/project/karuna/components/ui/button";
import { Input } from "@/project/karuna/components/ui/input";
import { PasswordInput } from "@/project/karuna/components/ui/password-input";
import { Label } from "@/project/karuna/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/project/karuna/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/project/karuna/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/project/karuna/components/ui/table";
import { Badge } from "@/project/karuna/components/ui/badge";
import { Checkbox } from "@/project/karuna/components/ui/checkbox";
import { Loader2, Pencil, Plus, Shield, Trash2 } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast";
import { TenantRoleService, TenantUserService } from "@/services/APIs";
import { getUserInfo } from "@/lib/storeManager";
import {
  SUPER_ADMIN_ROLE_NAME,
  assignSuperAdminRoleToUser,
  buildAdminUserPayload,
  deleteSuperAdminUser,
  extractCreatedUserId,
  fetchAllUserRoleMappings,
  findSuperAdminRole,
  getApiErrorMessage,
  getRoleIdsForUser,
  resolveUserIdByEmail,
  syncUserRoles,
  userHasSuperAdminRole,
} from "@/project/karuna/utils/adminPermissionUtils";

interface SuperAdminRow {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
}

const emptyForm = {
  email: "",
  password: "",
  firstName: "",
  lastName: "",
  is_active: true,
};

const AdminUsers = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentUserEmail = getUserInfo()?.email?.trim().toLowerCase() ?? "";

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SuperAdminRow | null>(null);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  const { data: superAdminRole, isLoading: roleLoading } = useQuery({
    queryKey: ["super-admin-role"],
    queryFn: async () => {
      const response = await TenantRoleService.listRoles(1, 200);
      return findSuperAdminRole(response.results ?? []);
    },
  });

  const {
    data: superAdmins = [],
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["super-admin-users", superAdminRole?.id],
    queryFn: async (): Promise<SuperAdminRow[]> => {
      if (!superAdminRole?.id) {
        return [];
      }

      const [usersResponse, roleMappings] = await Promise.all([
        TenantUserService.listTenantUsers(1, 500),
        fetchAllUserRoleMappings(),
      ]);

      const users = usersResponse?.users ?? usersResponse?.results ?? [];
      const allUsers = Array.isArray(users) ? users : [];

      return allUsers
        .filter((user: any) =>
          userHasSuperAdminRole(
            getRoleIdsForUser(roleMappings, user.id),
            superAdminRole.id
          )
        )
        .map((user: any) => ({
          id: user.id,
          email: user.email ?? "",
          first_name: user.first_name ?? "",
          last_name: user.last_name ?? "",
          is_active: user.is_active !== false,
        }));
    },
    enabled: Boolean(superAdminRole?.id),
  });

  const isSelf = (user: SuperAdminRow) =>
    user.email.trim().toLowerCase() === currentUserEmail;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["super-admin-users"] });
    queryClient.invalidateQueries({ queryKey: ["super-admin-role"] });
    refetchUsers();
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!superAdminRole?.id) {
        throw new Error(
          `"${SUPER_ADMIN_ROLE_NAME}" role not found. Create it in tenant permissions first.`
        );
      }
      const payload = buildAdminUserPayload({
        email: createForm.email,
        password: createForm.password,
        firstName: createForm.firstName,
        lastName: createForm.lastName,
      });
      // silent: suppress the generic global error toast so the friendly
      // message from onError (getApiErrorMessage) is the only one shown.
      const created = await TenantUserService.createUser(payload as any, {
        silent: true,
      });
      if (
        created &&
        typeof created === "object" &&
        "code" in created &&
        (created as { code?: string }).code === "validation_error"
      ) {
        throw new Error(
          getApiErrorMessage(
            { response: { data: created as Record<string, unknown> } },
            "Validation error."
          )
        );
      }

      let userId = extractCreatedUserId(created);
      userId ??= await resolveUserIdByEmail(createForm.email);
      if (!userId) {
        throw new Error(
          "User was saved but the system could not resolve the new user id to assign Super Admin role."
        );
      }

      await assignSuperAdminRoleToUser(userId, superAdminRole.id);
      return { id: userId };
    },
    onSuccess: () => {
      toast({
        title: "Super Admin created",
        description:
          "Account created and Super Admin role assigned. They can sign in at the admin login page.",
      });
      setCreateOpen(false);
      setCreateForm(emptyForm);
      invalidate();
    },
    onError: (error: any) => {
      toast({
        title: "Could not create Super Admin",
        description: getApiErrorMessage(error, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUser?.id || !superAdminRole?.id) {
        throw new Error("No user selected");
      }

      const payload: Record<string, unknown> = {
        email: editForm.email.trim(),
        first_name: editForm.firstName.trim(),
        last_name: editForm.lastName.trim(),
        full_name: [editForm.firstName, editForm.lastName]
          .filter(Boolean)
          .join(" "),
        user_type: "tenant_user",
        is_active: editForm.is_active,
      };

      payload.password = editForm.password.trim();

      await TenantUserService.updateUser(selectedUser.id, payload as any, {
        silent: true,
      });

      const mappings = await fetchAllUserRoleMappings();
      await syncUserRoles(selectedUser.id, [superAdminRole.id], mappings);
    },
    onSuccess: () => {
      toast({
        title: "Super Admin updated",
        description: "Administrator details have been saved.",
      });
      setEditOpen(false);
      setSelectedUser(null);
      invalidate();
    },
    onError: (error: any) => {
      toast({
        title: "Could not update administrator",
        description: getApiErrorMessage(error, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUser?.id) {
        throw new Error("No user selected");
      }
      if (isSelf(selectedUser)) {
        throw new Error("You cannot delete your own account.");
      }
      const mappings = await fetchAllUserRoleMappings();
      await deleteSuperAdminUser(selectedUser.id, mappings);
    },
    onSuccess: () => {
      toast({
        title: "Super Admin removed",
        description: "The administrator has been deleted.",
      });
      setDeleteOpen(false);
      setSelectedUser(null);
      invalidate();
    },
    onError: (error: any) => {
      toast({
        title: "Could not delete administrator",
        description: getApiErrorMessage(error, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const openEdit = (user: SuperAdminRow) => {
    setSelectedUser(user);
    setEditForm({
      email: user.email,
      password: "",
      firstName: user.first_name,
      lastName: user.last_name,
      is_active: user.is_active,
    });
    setEditOpen(true);
  };

  const openDelete = (user: SuperAdminRow) => {
    if (isSelf(user)) {
      toast({
        title: "Action not allowed",
        description: "You cannot delete your own account.",
        variant: "destructive",
      });
      return;
    }
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.email || !createForm.password || !createForm.firstName) {
      toast({
        title: "Missing fields",
        description: "Email, password, and first name are required.",
        variant: "destructive",
      });
      return;
    }
    createMutation.mutate();
  };

  const handleEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm.email || !editForm.firstName || !editForm.password.trim()) {
      toast({
        title: "Missing fields",
        description: "Email, first name, and password are required.",
        variant: "destructive",
      });
      return;
    }
    if (editForm.password.trim().length < 6) {
      toast({
        title: "Invalid password",
        description: "Password must be at least 6 characters.",
        variant: "destructive",
      });
      return;
    }
    editMutation.mutate();
  };

  const isLoading = usersLoading || roleLoading;
  const missingSuperAdminRole = !roleLoading && !superAdminRole;

  const pageDescription = useMemo(
    () =>
      missingSuperAdminRole
        ? `Create a "${SUPER_ADMIN_ROLE_NAME}" role in tenant permissions first.`
        : "Manage Super Admin accounts for the admin portal only.",
    [missingSuperAdminRole]
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-3xl font-bold">Super Administrators</h1>
            <p className="mt-1 text-muted-foreground">{pageDescription}</p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="gap-2"
            disabled={missingSuperAdminRole}
          >
            <Plus className="h-4 w-4" />
            Add Super Admin
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Super Admin Users
            </CardTitle>
            <CardDescription>
              Only users with the &quot;{SUPER_ADMIN_ROLE_NAME}&quot; role are
              shown. New accounts are created as Super Admins automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : missingSuperAdminRole ? (
              <p className="py-8 text-center text-muted-foreground">
                No &quot;{SUPER_ADMIN_ROLE_NAME}&quot; role found. Create it in
                Django admin under Tenant roles first.
              </p>
            ) : superAdmins.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                No Super Administrators yet. Click &quot;Add Super Admin&quot;
                to create one.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {superAdmins.map(user => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {[user.first_name, user.last_name]
                          .filter(Boolean)
                          .join(" ") || "—"}
                        {isSelf(user) && (
                          <Badge variant="outline" className="ml-2">
                            You
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {SUPER_ADMIN_ROLE_NAME}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_active ? "default" : "outline"}>
                          {user.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => openEdit(user)}
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1 text-destructive hover:text-destructive"
                            disabled={isSelf(user)}
                            onClick={() => openDelete(user)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Super Admin</DialogTitle>
            <DialogDescription>
              Creates an admin portal account and assigns the &quot;
              {SUPER_ADMIN_ROLE_NAME}&quot; role automatically.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="createFirstName">First Name</Label>
                <Input
                  id="createFirstName"
                  value={createForm.firstName}
                  onChange={e =>
                    setCreateForm(prev => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="createLastName">Last Name</Label>
                <Input
                  id="createLastName"
                  value={createForm.lastName}
                  onChange={e =>
                    setCreateForm(prev => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="createEmail">Email</Label>
              <Input
                id="createEmail"
                type="email"
                value={createForm.email}
                onChange={e =>
                  setCreateForm(prev => ({ ...prev, email: e.target.value }))
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="createPassword">Password</Label>
              <PasswordInput
                id="createPassword"
                value={createForm.password}
                onChange={e =>
                  setCreateForm(prev => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
                minLength={6}
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Super Admin"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Super Admin</DialogTitle>
            <DialogDescription>
              Update administrator details. Password is required on every save.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="editFirstName">First Name</Label>
                <Input
                  id="editFirstName"
                  value={editForm.firstName}
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editLastName">Last Name</Label>
                <Input
                  id="editLastName"
                  value={editForm.lastName}
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editEmail">Email</Label>
              <Input
                id="editEmail"
                type="email"
                value={editForm.email}
                onChange={e =>
                  setEditForm(prev => ({ ...prev, email: e.target.value }))
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editPassword">Password</Label>
              <PasswordInput
                id="editPassword"
                value={editForm.password}
                onChange={e =>
                  setEditForm(prev => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
                minLength={6}
                placeholder="Enter password"
                required
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={editForm.is_active}
                onCheckedChange={checked =>
                  setEditForm(prev => ({
                    ...prev,
                    is_active: checked === true,
                  }))
                }
              />
              Active account
            </label>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={editMutation.isPending}>
                {editMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Super Admin?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{" "}
              <strong>{selectedUser?.email}</strong> and revoke their Super
              Admin access. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
              onClick={e => {
                e.preventDefault();
                deleteMutation.mutate();
              }}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminUsers;
